import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { getAuth, updatePassword, updateProfile } from "firebase/auth";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DriveFileRenameOutlineRoundedIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import TopBarShell from "../components/Common/TopBarShell";
import colors from "../assets/theme/colors";
import { useNavigate } from "react-router-dom";

const getInitials = (value = "") => {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }

  const base = parts[0] || value.split("@")[0] || "";
  return base.slice(0, 2).toUpperCase();
};

const ProfileSectionCard = ({ icon, title, children }) => (
  <Box
    sx={{
      bgcolor: "#FFFFFF",
      border: `1px solid ${colors.semantic.line}`,
      borderRadius: 0,
      overflow: "hidden",
      mb: 1.75,
    }}
  >
    <Box
      sx={{
        px: 2.25,
        py: 1.75,
        display: "flex",
        alignItems: "center",
        gap: 1.1,
        borderBottom: `1px solid ${colors.semantic.mist}`,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 0,
          bgcolor: colors.semantic.sageSoft,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: colors.landing.heroText }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 2.25, py: 2.25 }}>{children}</Box>
  </Box>
);

const ProfilePage = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  const fileInputRef = useRef(null);

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [loadingSection, setLoadingSection] = useState("");
  const [feedback, setFeedback] = useState({ section: "", type: "", text: "" });

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || "");
      setEmail(user.email || "");
      setPhotoURL(user.photoURL || "");
    }
  }, [user]);

  const nameChanged = useMemo(
    () => displayName.trim() !== (user?.displayName || "").trim(),
    [displayName, user?.displayName]
  );

  const avatarLabel = useMemo(() => getInitials(displayName || user?.email || ""), [displayName, user?.email]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/dashboard");
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoadingSection("name");
    setFeedback({ section: "", type: "", text: "" });

    try {
      if (user) {
        await updateProfile(user, { displayName: displayName.trim() });
        setFeedback({ section: "name", type: "success", text: "Name updated" });
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      setFeedback({ section: "name", type: "error", text: "Failed to update name." });
    } finally {
      setLoadingSection("");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoadingSection("password");
    setFeedback({ section: "", type: "", text: "" });

    if (newPassword !== confirmNewPassword) {
      setFeedback({ section: "password", type: "error", text: "Passwords don't match" });
      setLoadingSection("");
      return;
    }

    if (newPassword.length < 6) {
      setFeedback({ section: "password", type: "error", text: "Password should be at least 6 characters." });
      setLoadingSection("");
      return;
    }

    try {
      if (user && newPassword) {
        await updatePassword(user, newPassword);
        setFeedback({ section: "password", type: "success", text: "Password updated" });
        setNewPassword("");
        setConfirmNewPassword("");
      }
    } catch (err) {
      console.error("Error changing password:", err);
      if (err.code === "auth/requires-recent-login") {
        setFeedback({
          section: "password",
          type: "error",
          text: "Please re-authenticate to change your password.",
        });
      } else {
        setFeedback({ section: "password", type: "error", text: "Failed to update password." });
      }
    } finally {
      setLoadingSection("");
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file || !user) return;

    setLoadingSection("photo");
    setFeedback({ section: "", type: "", text: "" });

    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });

      await updateProfile(user, { photoURL: dataUrl });
      setPhotoURL(dataUrl);
      setFeedback({ section: "avatar", type: "success", text: "Photo updated" });
    } catch (error) {
      console.error("Error updating avatar:", error);
      setFeedback({ section: "avatar", type: "error", text: "Could not update photo." });
    } finally {
      setLoadingSection("");
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: colors.landing.pageBackground }}>
      <TopBarShell
        showShadow={false}
        appBarSx={{
          bgcolor: "rgba(246,248,246,0.95)",
          backdropFilter: "blur(14px)",
          borderBottom: `1px solid ${colors.semantic.line}`,
          boxShadow: "none",
        }}
        toolbarSx={{
          minHeight: 48,
          height: 48,
          px: { xs: 1.25, sm: 2 },
        }}
        left={
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            sx={{
              width: 34,
              height: 34,
          borderRadius: 0,
              bgcolor: "#FFFFFF",
              border: `1px solid ${colors.semantic.line}`,
              color: colors.landing.heroText,
              boxShadow: "none",
              "&:hover": {
                bgcolor: colors.landing.surfaceSoft,
              },
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        }
        center={
          <Typography
            sx={{
              fontSize: 17,
              fontWeight: 700,
              letterSpacing: "-0.3px",
              color: colors.landing.heroText,
            }}
          >
            Profile settings
          </Typography>
        }
        right={<Box sx={{ width: 34, height: 34 }} />}
      />

      <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 10 }}>
        <Box
          sx={{
            background: "linear-gradient(160deg, #F5F0E8 0%, #EAF2EE 100%)",
            borderRadius: 0,
            px: 2,
            pt: 3.5,
            pb: 3,
            textAlign: "center",
            border: `1px solid ${colors.semantic.line}`,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
            mb: 2,
          }}
        >
          <Box sx={{ position: "relative", width: 80, height: 80, mx: "auto", mb: 1.5 }}>
            <Avatar
              src={photoURL || undefined}
              sx={{
                width: 80,
                height: 80,
                borderRadius: 0,
                border: "3px solid #FFFFFF",
                bgcolor: colors.semantic.sageSoft,
                color: colors.semantic.sageDeep,
                fontSize: "1.75rem",
                fontWeight: 700,
                boxShadow: "0 8px 22px rgba(15, 23, 42, 0.12)",
              }}
            >
              {!photoURL ? avatarLabel : null}
            </Avatar>
            <IconButton
              onClick={handleAvatarClick}
              aria-label="Change profile photo"
              sx={{
                position: "absolute",
                right: -2,
                bottom: -2,
                width: 26,
                height: 26,
                bgcolor: colors.semantic.sageDeep,
                border: "2px solid #FFFFFF",
                color: "#FFFFFF",
                boxShadow: "0 4px 10px rgba(111, 165, 148, 0.26)",
                "&:hover": {
                  bgcolor: "#5C9683",
                },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 13, strokeWidth: 2.5 }} />
            </IconButton>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleAvatarFile}
            />
          </Box>

          <Typography sx={{ fontSize: 18, fontWeight: 700, color: colors.landing.heroText, letterSpacing: "-0.3px" }}>
            {displayName || user?.displayName || "Your profile"}
          </Typography>
          <Typography sx={{ mt: 0.4, fontSize: 13, color: colors.semantic.ink2 }}>
            {email || user?.email || ""}
          </Typography>
          <Chip
            icon={<PeopleAltRoundedIcon sx={{ fontSize: 15 }} />}
            label="Primary caregiver"
            sx={{
              mt: 1.4,
              bgcolor: colors.semantic.sageSoft,
              color: colors.semantic.sageDeep,
              fontWeight: 650,
              fontSize: "0.72rem",
                  borderRadius: 0,
              px: 0.5,
              height: 28,
              "& .MuiChip-icon": {
                color: colors.semantic.sageDeep,
                ml: 0.75,
              },
            }}
          />
          {feedback.section === "avatar" ? (
            <Box
              sx={{
                mt: 1.5,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                color: feedback.type === "success" ? colors.semantic.success : colors.semantic.error,
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              {feedback.type === "success" ? <DoneRoundedIcon sx={{ fontSize: 16 }} /> : <ErrorOutlineRoundedIcon sx={{ fontSize: 16 }} />}
              {feedback.text}
            </Box>
          ) : null}
        </Box>

        <ProfileSectionCard
          icon={<DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 15, color: colors.semantic.sageDeep, strokeWidth: 2.2 }} />}
          title="Display name"
        >
          <Box component="form" onSubmit={handleUpdateProfile} noValidate>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.2, color: colors.semantic.ink, mb: 0.8 }}>
              Name
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Display name"
                disabled={!user}
                InputProps={{
                  sx: {
                    height: 50,
                    borderRadius: 0,
                    bgcolor: "#FFFFFF",
                    fontSize: 15,
                    "& input": {
                      py: 1.4,
                    },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 0,
                    "& fieldset": {
                      borderColor: colors.semantic.line,
                      borderWidth: 1.5,
                    },
                    "&:hover fieldset": {
                      borderColor: colors.semantic.borderMedium,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.semantic.sageDeep,
                    },
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={!user || !nameChanged || !displayName.trim() || loadingSection === "name"}
                sx={{
                  height: 50,
                  minWidth: 86,
                  px: 2.25,
                  borderRadius: 0,
                  bgcolor: colors.semantic.sageDeep,
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  boxShadow: "0 4px 12px rgba(111,165,148,0.25)",
                  "&:hover": {
                    bgcolor: "#5C9683",
                  },
                  "&.Mui-disabled": {
                    bgcolor: colors.semantic.line,
                    color: colors.semantic.ink2,
                    boxShadow: "none",
                  },
                }}
              >
                Save
              </Button>
            </Box>
            {feedback.section === "name" ? (
              <Box
                sx={{
                  mt: 1.25,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: feedback.type === "success" ? colors.semantic.success : colors.semantic.error,
                  fontSize: 12.5,
                  fontWeight: 600,
                }}
              >
                {feedback.type === "success" ? <DoneRoundedIcon sx={{ fontSize: 16 }} /> : <ErrorOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                {feedback.text}
              </Box>
            ) : null}
          </Box>
        </ProfileSectionCard>

        <ProfileSectionCard
          icon={<LockRoundedIcon sx={{ fontSize: 15, color: colors.semantic.sageDeep, strokeWidth: 2.2 }} />}
          title="Change password"
        >
          <Box component="form" onSubmit={handleChangePassword} noValidate>
            <Box sx={{ display: "grid", gap: 1.4 }}>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.2, color: colors.semantic.ink, mb: 0.8 }}>
                  New password
                </Typography>
                <TextField
                  fullWidth
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={!user}
                  InputProps={{
                    sx: {
                      height: 50,
                      borderRadius: 0,
                      bgcolor: "#FFFFFF",
                      fontSize: 15,
                      "& input": {
                        py: 1.4,
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                      "& fieldset": {
                        borderColor: colors.semantic.line,
                        borderWidth: 1.5,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.semantic.borderMedium,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.semantic.sageDeep,
                      },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.2, color: colors.semantic.ink, mb: 0.8 }}>
                  Confirm new password
                </Typography>
                <TextField
                  fullWidth
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  disabled={!user}
                  InputProps={{
                    sx: {
                      height: 50,
                      borderRadius: 0,
                      bgcolor: "#FFFFFF",
                      fontSize: 15,
                      "& input": {
                        py: 1.4,
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                      "& fieldset": {
                        borderColor: colors.semantic.line,
                        borderWidth: 1.5,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.semantic.borderMedium,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.semantic.sageDeep,
                      },
                    },
                  }}
                />
              </Box>

              {feedback.section === "password" && feedback.type === "error" ? (
                <Typography sx={{ color: "#D17A7A", fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}>
                  {feedback.text}
                </Typography>
              ) : null}

              <Button
                type="submit"
                variant="contained"
                disabled={
                  !user ||
                  !newPassword.trim() ||
                  !confirmNewPassword.trim() ||
                  newPassword !== confirmNewPassword ||
                  loadingSection === "password"
                }
                sx={{
                  height: 50,
                  borderRadius: 0,
                  bgcolor: colors.semantic.sageDeep,
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: "none",
                  boxShadow: "0 4px 12px rgba(111,165,148,0.25)",
                  "&:hover": {
                    bgcolor: "#5C9683",
                  },
                  "&.Mui-disabled": {
                    bgcolor: colors.semantic.line,
                    color: colors.semantic.ink2,
                    boxShadow: "none",
                  },
                }}
              >
                Update password
              </Button>
              {feedback.section === "password" && feedback.type === "success" ? (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    color: colors.semantic.success,
                    fontSize: 12.5,
                    fontWeight: 600,
                  }}
                >
                  <DoneRoundedIcon sx={{ fontSize: 16 }} />
                  {feedback.text}
                </Box>
              ) : null}
            </Box>
          </Box>
        </ProfileSectionCard>

        <ProfileSectionCard
          icon={<EmailRoundedIcon sx={{ fontSize: 15, color: colors.semantic.sageDeep, strokeWidth: 2.2 }} />}
          title="Email address"
        >
          <TextField
            fullWidth
            value={email}
            InputProps={{
              readOnly: true,
              sx: {
                height: 50,
                borderRadius: 0,
                bgcolor: colors.semantic.mist,
                color: colors.semantic.ink,
                fontSize: 15,
                "& input": {
                  py: 1.4,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                "& fieldset": {
                  borderColor: colors.semantic.line,
                  borderWidth: 1.5,
                },
              },
            }}
          />
          <Typography sx={{ mt: 1.1, fontSize: 12, color: colors.semantic.ink2, lineHeight: 1.45 }}>
            To change your email, contact support.
          </Typography>
        </ProfileSectionCard>

        <Box
          sx={{
            mt: 1.5,
            bgcolor: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: 0,
            p: 1.75,
            mb: 1,
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#B45050", mb: 1.25 }}>
            Danger zone
          </Typography>
          <Button
            fullWidth
            variant="outlined"
            sx={{
              height: 44,
              borderRadius: 0,
              borderColor: "#FECACA",
              color: "#B45050",
              bgcolor: "#FFFFFF",
              fontWeight: 650,
              fontSize: 14,
              textTransform: "none",
              "&:hover": {
                borderColor: "#F8A8A8",
                bgcolor: "#FFF7F7",
              },
            }}
          >
            {/* TODO: wire up account deletion flow with confirmation and backend support. */}
            Delete account
          </Button>
        </Box>

      </Container>
    </Box>
  );
};

export default ProfilePage;
